import React from 'react';

/**
 * Wrapper that uses CSS `content-visibility: auto` to defer rendering of
 * off-screen sections without unmounting them. Keeps the DOM intact for
 * anchor links, in-page search, screen readers and SEO crawlers.
 *
 * `contain-intrinsic-size` reserves the height before the section is
 * painted, so CLS stays at zero.
 */
interface LazySectionProps {
  children: React.ReactNode;
  /** Reserved height before the section renders (desktop). Default: 600px. */
  minHeight?: number;
  /** Reserved height on mobile (<768px). Defaults to min(minHeight, 320). */
  mobileMinHeight?: number;
  className?: string;
}

const LazySection: React.FC<LazySectionProps> = ({
  children,
  minHeight = 600,
  mobileMinHeight,
  className,
}) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const height = isMobile ? (mobileMinHeight ?? Math.min(minHeight, 320)) : minHeight;
  return (
    <div
      className={className}
      style={{
        contentVisibility: 'auto',
        containIntrinsicSize: `${height}px`,
      }}
    >
      {children}
    </div>
  );
};

export default LazySection;
